import { cgTemplate, insideOf, isOverlayKind, storylineCg, type StorylineBlock } from '@airpost/shared';

/**
 * The storyline as it goes on air: one segment per box, in order, each with a start and a length in seconds, the
 * pictures on screen (the box's own clip, or the pictures that keep rolling from the last video box while the
 * presenter speaks, a title shows, a credit runs) and what is laid over them (the autocue text, a CG strap, a
 * full-screen card). Any box given a second sits inside the box before it ("anything into anything"): it plays in
 * that box's segment from its second for its own length and takes no time of its own; a video inside takes the
 * picture for its length (a VO inside the presenter's ON plays in a window beside the presenter instead, the way the
 * mixer puts it: picture in picture, ADR 091), and a box whose contents run past its end grows to hold them (a title
 * is pulled back into its box instead, as before). Pure, so the player and the tests share it.
 */

/** The newsroom reads 3.3 words a second (the rule the editor's reading time uses). */
export const WORDS_PER_SECOND = 3.3;
/** A video box without a clip holds the screen this long, so it is seen. */
export const NO_CLIP_S = 5;
export const NATSOUND_S = 3;
export const PROMO_S = 5;
/** A statement with a name but no words yet. */
export const SOT_MIN_S = 5;

export interface TimelineCue {
  mediaAssetId: string | null;
  inMs: number | null;
  outMs: number | null;
  durationMs: number | null;
  media: { durationMs: number } | null;
  /** The clip's edit, when the storyline plays one; rendered = its file exists, renderedAt = which export it is, plain = it airs as the clip itself, cut. */
  edit?: { id: string; rendered: boolean; durationMs: number | null; renderedAt?: string | null; plain?: boolean } | null;
}

export type Overlay =
  | { kind: 'prompter'; mode: 'on' | 'vo'; text: string }
  | { kind: 'cg'; template: string; lines: string[] }
  | { kind: 'badge'; text: string }
  | { kind: 'placeholder'; text: string };

export interface Clip {
  mediaAssetId: string;
  /** Where the clip starts (its in point), seconds. */
  inS: number;
  /** How long the clip runs from its in point, seconds. */
  lengthS: number;
  /** The timeline second at which the clip started rolling (its own box), so continuing pictures stay in step. */
  rolledAt: number;
  /** Its sound is heard (a ΒΙΝΤΕΟ ΜΕ ΗΧΟ box, not spoken over). */
  sound: boolean;
  /** The box this segment belongs to is the clip's own box. */
  own: boolean;
  /** The rendered edit to play instead of the source, when there is one. */
  editId: string | null;
  /** Which export of that edit (its time), so the player asks for the new file after another export. */
  editAt: string | null;
}

/** A box inside a segment: from `atS` into the segment for `durationS`; a video brings its clip, the rest an overlay. */
export interface RidingOverlay {
  index: number;
  box: StorylineBlock;
  atS: number;
  durationS: number;
  overlay: Overlay | null;
  /** A video inside: its clip takes the picture while it runs, or plays in a window when `pip`. */
  clip: Clip | null;
  /** A VO inside the presenter's ON: picture in picture, the presenter stays on camera. */
  pip: boolean;
  label: string;
}

export interface Segment {
  index: number;
  box: StorylineBlock;
  start: number;
  duration: number;
  clip: Clip | null;
  overlay: Overlay | null;
  /** Titles riding on this segment, by their time. */
  riders: RidingOverlay[];
  /** Pictures from the studio: no clip is on screen. */
  studio: boolean;
  label: string;
}

export const words = (s: string): number => (s.match(/\S+/g) ?? []).length;
export const readingSeconds = (s: string): number => Math.max(1, Math.round(words(s) / WORDS_PER_SECOND));

export function segmentLabel(b: StorylineBlock): string {
  switch (b.kind) {
    case 'read':
      return b.mode === 'on' ? 'ON' : 'ΣΠΗΚΑΖ';
    case 'video':
      return b.sound ? 'ΒΙΝΤΕΟ ΜΕ ΗΧΟ' : 'ΒΙΝΤΕΟ VO';
    case 'title':
      return b.n !== null ? `ΤΙΤΛΟΣ ${b.n}` : 'ΤΙΤΛΟΣ';
    case 'sot':
      return 'ΔΗΛΩΣΗ';
    case 'reporter':
      return 'ΡΕΠΟΡΤΑΖ';
    case 'card':
      return b.n !== null ? `ΚΑΡΤΑ ${b.n}` : 'ΚΑΡΤΑ';
    case 'natsound':
      return 'ΦΥΣΙΚΟΣ ΗΧΟΣ';
    case 'promo':
      return 'ALPHANEWS.LIVE';
    case 'note':
      return '';
  }
}

/** How long a box holds the air, in seconds; 0 = it takes no time (a note, an empty read). */
export function boxDuration(b: StorylineBlock, cue: TimelineCue | null): number {
  switch (b.kind) {
    case 'read':
      return b.text.trim() ? readingSeconds(b.text) : 0;
    case 'sot':
      return b.text.trim() ? readingSeconds(b.text) : b.name.trim() ? SOT_MIN_S : 0;
    case 'video': {
      if (!cue?.mediaAssetId) return NO_CLIP_S;
      if (cue.edit?.rendered && cue.edit.durationMs) return Math.max(1, Math.round(cue.edit.durationMs / 1000));
      const length = cue.media ? cue.media.durationMs : null;
      const end = cue.outMs ?? length;
      if (end === null) return NO_CLIP_S;
      return Math.max(1, Math.round((end - (cue.inMs ?? 0)) / 1000));
    }
    case 'title':
    case 'reporter':
    case 'card': {
      const ms = cue?.durationMs ?? cgTemplate(storylineCg(b)?.template)?.defaultMs ?? 7000;
      return Math.max(1, Math.round(ms / 1000));
    }
    case 'natsound':
      return NATSOUND_S;
    case 'promo':
      return PROMO_S;
    case 'note':
      return 0;
  }
}

/** The pictures keep rolling under these boxes; the studio (ON) and a full-screen card replace them. */
const keepsPictures = (b: StorylineBlock): boolean => (b.kind === 'read' ? b.mode === 'vo' : b.kind !== 'card' && b.kind !== 'video');

export function overlayOf(b: StorylineBlock, cue: TimelineCue | null): Overlay | null {
  switch (b.kind) {
    case 'read':
      return { kind: 'prompter', mode: b.mode, text: b.text };
    case 'sot': {
      const cg = storylineCg(b);
      return b.text.trim() ? { kind: 'prompter', mode: 'vo', text: b.text } : cg && cg.lines.length ? { kind: 'cg', template: cg.template, lines: cg.lines } : null;
    }
    case 'title':
    case 'reporter':
    case 'card': {
      const cg = storylineCg(b);
      return cg ? { kind: 'cg', template: cg.template, lines: cg.lines } : null;
    }
    case 'video':
      return cue?.mediaAssetId ? null : { kind: 'placeholder', text: b.note };
    case 'natsound':
      return { kind: 'badge', text: 'ΦΥΣΙΚΟΣ ΗΧΟΣ' };
    case 'promo':
      return { kind: 'cg', template: 'live', lines: ['ALPHANEWS.LIVE'] };
    case 'note':
      return null;
  }
}

/** The second a box asks to come in inside the box before it: a title's on its CG cue, any other box's in the story's map. */
export type AtFor = (index: number) => number | null;
export const rideAtMs = (b: StorylineBlock, cue: TimelineCue | null, atFor: AtFor | undefined, index: number): number | null =>
  isOverlayKind(b.kind) ? (cue?.inMs ?? null) : (atFor?.(index) ?? null);

/** Which box sits inside which (box index → carrier index). */
export function ridersOf(blocks: readonly StorylineBlock[], cueFor: (index: number) => TimelineCue | null, atFor?: AtFor): Map<number, number> {
  return insideOf(blocks, (i) => rideAtMs(blocks[i]!, cueFor(i), atFor, i));
}

/** The clip of a video box (its own, or its rendered edit), rolling from `rolledAt`; null when it has none yet. */
function clipOf(box: StorylineBlock, cue: TimelineCue | null, lengthS: number, rolledAt: number): Clip | null {
  if (box.kind !== 'video' || !cue?.mediaAssetId) return null;
  // a plain cut airs as the clip itself under the story's titles, so it plays so here too (its export has the straps drawn in)
  const rendered = cue.edit?.rendered && !cue.edit.plain ? cue.edit : null;
  return { mediaAssetId: cue.mediaAssetId, inS: rendered ? 0 : (cue.inMs ?? 0) / 1000, lengthS, rolledAt, sound: box.sound, own: true, editId: rendered ? rendered.id : null, editAt: rendered?.renderedAt ?? null };
}

/** The segments of a storyline, in order, with the pictures that continue from the last clip. */
export function buildTimeline(blocks: StorylineBlock[], cueFor: (index: number) => TimelineCue | null, atFor?: AtFor): Segment[] {
  const out: Segment[] = [];
  const riders = ridersOf(blocks, cueFor, atFor);
  // what sits inside each carrier, and how far it reaches (a box whose contents run past its end grows to hold them)
  const reach = new Map<number, number>();
  for (const [index, carrier] of riders) {
    const box = blocks[index]!;
    // titles are pulled back into their box instead; a window beside the presenter closes when the presenter is done
    if (isOverlayKind(box.kind) || isPip(box, blocks[carrier]!)) continue;
    const cue = cueFor(index);
    const end = (rideAtMs(box, cue, atFor, index) ?? 0) / 1000 + boxDuration(box, cue);
    reach.set(carrier, Math.max(reach.get(carrier) ?? 0, end));
  }
  let t = 0;
  let rolling: Clip | null = null;
  blocks.forEach((box, index) => {
    const cue = cueFor(index);
    if (riders.has(index)) return; // it plays inside its carrier's segment
    const own = boxDuration(box, cue);
    const duration = Math.max(own, Math.ceil(reach.get(index) ?? 0));
    if (duration <= 0) return;
    let clip: Clip | null = null;
    if (box.kind === 'video') {
      rolling = clipOf(box, cue, duration, t);
      clip = rolling;
    } else if (rolling && keepsPictures(box)) {
      // the pictures continue; under spoken words their sound goes down
      const spoken = box.kind === 'read' || box.kind === 'sot';
      clip = { ...rolling, own: false, sound: rolling.sound && !spoken };
    } else if (box.kind === 'card') {
      clip = null; // the card fills the screen; the pictures resume after it
    } else {
      rolling = null; // the studio: the pictures are gone
    }
    const studio = !clip && box.kind !== 'card' && !(box.kind === 'video');
    out.push({ index, box, start: t, duration, clip, overlay: overlayOf(box, cue), riders: [], studio, label: segmentLabel(box) });
    t += duration;
  });
  // what sits inside each segment, by its second (a title past the end is pulled back so it is seen)
  const byIndex = new Map(out.map((s) => [s.index, s]));
  for (const [index, carrier] of riders) {
    const seg = byIndex.get(carrier);
    const box = blocks[index]!;
    const cue = cueFor(index);
    if (!seg) continue;
    const askedS = (rideAtMs(box, cue, atFor, index) ?? 0) / 1000;
    const atS = isOverlayKind(box.kind) ? Math.max(0, Math.min(askedS, Math.max(0, seg.duration - 1))) : Math.max(0, askedS);
    // the window beside the presenter runs for its clip, but no longer than the presenter speaks
    const windowed = isPip(box, seg.box);
    const durationS = windowed ? Math.min(boxDuration(box, cue), seg.duration - atS) : boxDuration(box, cue);
    if (durationS <= 0) continue;
    const clip = clipOf(box, cue, durationS, seg.start + atS);
    const overlay = clip ? null : overlayOf(box, cue);
    if (!clip && !overlay) continue;
    seg.riders.push({ index, box, atS, durationS, overlay, clip, pip: windowed && !!clip, label: segmentLabel(box) });
  }
  for (const s of out) s.riders.sort((a, b) => a.atS - b.atS || a.index - b.index);
  return out;
}

/** A VO video inside the presenter's ON plays picture in picture: the presenter stays on camera, the pictures in a window. */
export const isPip = (rider: StorylineBlock, carrier: StorylineBlock): boolean => rider.kind === 'video' && !rider.sound && carrier.kind === 'read' && carrier.mode === 'on';

/** What sits inside a segment and is on `elapsed` seconds into it. */
export const ridersAt = (seg: Segment, elapsed: number): RidingOverlay[] => seg.riders.filter((r) => elapsed >= r.atS && elapsed < r.atS + r.durationS);

/** The picture `elapsed` seconds into a segment: a video sitting inside it while it runs (the last to start, a window aside), else the segment's own. */
export function clipAt(seg: Segment, elapsed: number): Clip | null {
  const inside = ridersAt(seg, elapsed).filter((r) => r.clip && !r.pip);
  return inside.length ? inside[inside.length - 1]!.clip : seg.clip;
}

/** The window `elapsed` seconds into a segment: the VO playing beside the presenter (the last to start), or none. */
export function pipAt(seg: Segment, elapsed: number): Clip | null {
  const inside = ridersAt(seg, elapsed).filter((r) => r.clip && r.pip);
  return inside.length ? inside[inside.length - 1]!.clip : null;
}

export const timelineTotal = (segments: Segment[]): number => (segments.length ? segments[segments.length - 1]!.start + segments[segments.length - 1]!.duration : 0);

/** The segment on air at second `t` (the last one at the very end). */
export function segmentAt(segments: Segment[], t: number): Segment | null {
  if (!segments.length) return null;
  for (const s of segments) if (t < s.start + s.duration) return s;
  return segments[segments.length - 1]!;
}

/** How many words of a spoken box have been said `elapsed` seconds into it. */
export const wordsSaid = (text: string, elapsed: number): number => Math.min(words(text), Math.floor(Math.max(0, elapsed) * WORDS_PER_SECOND));

/** How many words the yellow of the prompter takes to cool back to white behind the reading. */
export const READING_TRAIL_WORDS = 2.5;
/** The yellow starts warming a word this far (in words) before the reading reaches it, so the light flows into it. */
const READING_LEAD_WORDS = 0.35;

/**
 * The prompter's light on a word, by how far the reading is past the word's start (in words, with the fraction):
 * ahead it is dim white; as the reading arrives it warms to yellow; behind the reading it cools back to white over
 * READING_TRAIL_WORDS. Continuous, so the yellow glides through the text instead of jumping from word to word.
 * Returns the green and blue of a colour whose red is full, and its alpha.
 */
export function readingLight(past: number): [green: number, blue: number, alpha: number] {
  const warm = past + READING_LEAD_WORDS;
  if (warm <= 0) return [255, 255, 0.5];
  if (warm < 1) return [255 - 46 * warm, 255 - 153 * warm, 0.5 + 0.5 * warm];
  const heat = Math.max(0, 1 - (warm - 1) / READING_TRAIL_WORDS);
  return [255 - 46 * heat, 255 - 153 * heat, 1];
}
